<template>
    <div class="innovation-rank-tab w-100">
        <p class="my-md ml-sm">{{ description }}</p>
        <div class="display-flex flex-row chart-container">
            <BenchmarkingMomentumChart
                class=""
                :dataLoading="dataLoading"
                :chart-data="chartData"
                :competitors="competitors"
            />
            <TimelineXYChartAM
                class=""
                :chart-data="competitorTimelineChartData"
                :loading="loadingTimeline"
                :entityId="entityStore.entity.entityPk"
            />
        </div>
        <BenchmarkingCompetitorTable
            class="mt-md"
            ref="competitorTableRef"
            :tech-area-ids="techAreaIds"
            :table-data="tableData"
            :loading="tableLoading"
            :quick-filter="quickFilter"
            @filter-change="$emit('filter-change', $event)"
        />
    </div>
</template>

<script setup>
import BenchmarkingMomentumChart from '../charts/BenchmarkingMomentumChart.vue'
import BenchmarkingCompetitorTable from '../components/BenchmarkingCompetitorTable.vue'
import TimelineXYChartAM from '@/components/alignmentOutput/benchmarkPeers/charts/Timeline.vue'
import { ref, defineProps, defineEmits, computed, watch, inject } from 'vue'
import { useEntityStore, useMoat2ProductStore } from '@/stores'
import { getCompetitorsTimeline } from '@/api/productAlignment.js'

const entityStore = useEntityStore()
const moat2ProductStore = useMoat2ProductStore()
const logger = inject('logger')
const props = defineProps({
    dataLoading: {
        type: Boolean,
        default: false,
    },
    chartData: {
        type: Array,
        default: () => [],
    },
    competitors: {
        type: Array,
        default: () => [],
    },
    tableData: {
        type: Array,
        default: () => [],
    },
    tableLoading: {
        type: Boolean,
        default: false,
    },
    techAreaIds: {
        type: Array,
        default: () => [],
    },
    quickFilter: {
        type: String,
        default: 'all',
    },
})

const loadingTimeline = ref(true)
const competitorTimelineChartData = ref([])

const getCompetitorsCumulativePatentSummaryByYear = async (newIds = false) => {
    loadingTimeline.value = true
    try {
        const { data } = await getCompetitorsTimeline(
            moat2ProductStore.alignmentPk,
            props.techAreaIds,
            props.competitors.map((item) => item.aonEntityPk)
        )
        competitorTimelineChartData.value = data.filter((item) =>
            props.competitors.map((item) => item.aonEntityPk).includes(item.aonEntityPk)
        )
    } catch (err) {
        logger.error(err)
    } finally {
        loadingTimeline.value = false
    }
}

defineEmits(['filter-change'])

const competitorTableRef = ref(null)

const description = computed(() => {
    return `Visualize the Moat Coverage over time for the top five companies in ${entityStore.entity.name}'s product space, including their entry, intensity, and decline of patent filings. The thickness of each band represents the relative volume or impact of filings over time.`
})

watch(
    () => [props.competitors],
    async ([newVal]) => {
        if (newVal.length > 0) {
            await getCompetitorsCumulativePatentSummaryByYear()
        }
    },
    { immediate: true }
)

defineExpose({
    competitorTableRef,
})
</script>

<style lang="scss" scoped>
.chart-container {
    height: 500px;
}
</style>
